<template>
  <div class="advance-ledger-report">
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ reportTitle }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ ledgerItems.length }} Entries
        </b-badge>
        <b-badge v-if="syncing" variant="light-warning" class="ml-2">
          <b-spinner small class="mr-25" />
          Syncing ledger...
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">Loading ledger details...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">{{ reportTitle }}</span>
              <span class="date-range-text">
                <strong>{{ reportData.accountTitle }}</strong> |
                <strong>{{ formatDate(dtFrom) }}</strong> to
                <strong>{{ formatDate(dtTo) }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Balance Summary -->
      <div class="balance-summary mb-3">
        <div class="balance-card">
          <span class="label">Opening Balance:</span>
          <span class="value text-primary">{{
            formatCurrency(reportData.openingBalance)
          }}</span>
        </div>
        <div class="balance-card">
          <span class="label">Closing Balance:</span>
          <span class="value text-success">{{
            formatCurrency(reportData.closingBalance)
          }}</span>
        </div>
      </div>

      <!-- Ledger Table -->
      <div class="ledger-table-wrap">
        <table class="ledger-table">
          <thead>
            <tr class="main-header">
              <th class="text-center col-vno">V.NO</th>
              <th class="col-date">DATE</th>
              <th class="col-cheque">CHEQUE TITLE</th>
              <th class="col-desc">DESCRIPTION</th>
              <th class="text-right col-amount">DEBIT</th>
              <th class="text-right col-amount">CREDIT</th>
              <th class="text-right col-amount">BALANCE</th>
            </tr>
          </thead>
          <tbody>
            <!-- Opening Balance Row -->
            <tr class="opening-row">
              <td class="text-center">-</td>
              <td class="font-weight-bold">
                {{
                  ledgerItems.length > 0
                    ? formatDate(ledgerItems[0].date)
                    : formatDate(dtFrom)
                }}
              </td>
              <td class="text-center">-</td>
              <td class="cell-desc text-italic">Opening Balance B/F</td>
              <td class="text-right text-muted col-amount">-</td>
              <td class="text-right text-muted col-amount">-</td>
              <td class="text-right font-weight-bold col-amount">
                {{ formatCurrency(reportData.openingBalance) }}
              </td>
            </tr>

            <!-- Data Rows -->
            <tr v-for="(item, index) in ledgerItems" :key="index">
              <td class="text-center cell-vno">
                <a
                  href="#"
                  class="voucher-link"
                  @click.prevent="openVoucherMenu(item)"
                >
                  {{ item.vNo || "-" }}
                </a>
              </td>
              <td class="cell-date">{{ formatDate(item.date) }}</td>
              <td class="cell-cheque">
                {{ (item && item.chequeTitle) || "-" }}
              </td>
              <td class="cell-desc">{{ item.description || "-" }}</td>
              <td class="text-right text-danger font-weight-bold col-amount">
                {{ item.debit > 0 ? formatCurrency(item.debit) : "-" }}
              </td>
              <td class="text-right text-success font-weight-bold col-amount">
                {{ item.credit > 0 ? formatCurrency(item.credit) : "-" }}
              </td>
              <td class="text-right font-weight-bold col-amount">
                {{ formatBalance(item.balance) }}
              </td>
            </tr>

            <!-- If No Data -->
            <tr v-if="ledgerItems.length === 0">
              <td colspan="7" class="text-center py-4 text-muted">
                No transactions found for this period.
              </td>
            </tr>

            <!-- Totals Row (in tbody so it prints only on the last page) -->
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold">TOTALS</td>
              <td class="text-right font-weight-bold col-amount">
                {{ formatCurrency(totalDebit) }}
              </td>
              <td class="text-right font-weight-bold col-amount">
                {{ formatCurrency(totalCredit) }}
              </td>
              <td class="text-right font-weight-bold bg-light col-amount">
                {{ formatCurrency(reportData.closingBalance) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <div class="page-footer">
        <small
          >Powered by MySkool.app | Generated On: {{ currentDateTime }}</small
        >
      </div>
    </div>

    <!-- Voucher action menu -->
    <b-modal
      v-model="showVoucherAction"
      title="Voucher"
      size="sm"
      centered
      hide-footer
      class="no-print voucher-action-modal"
      @hidden="selectedVoucherItem = null"
    >
      <div v-if="selectedVoucherItem" class="voucher-action-body">
        <p class="voucher-action-label mb-3">
          Voucher <strong>{{ selectedVoucherItem.vNo || "-" }}</strong>
        </p>
        <div class="voucher-action-buttons">
          <b-button
            variant="outline-primary"
            block
            class="mb-1"
            @click="printSelectedVoucher"
          >
            <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
            Print
          </b-button>
          <b-button
            variant="primary"
            block
            :disabled="editLoading"
            @click="editSelectedVoucher"
          >
            <b-spinner v-if="editLoading" small class="mr-50" />
            <feather-icon v-else icon="EditIcon" size="16" class="mr-50" />
            Edit
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge, BModal } from "bootstrap-vue";
import axios from "axios";

const JOURNAL_EDIT_STORAGE_PREFIX = "myskool-journal-edit-data-";

export default {
  name: "AdvanceLedgerPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
    BModal,
  },
  data() {
    return {
      loading: false,
      syncing: false,
      editLoading: false,
      showVoucherAction: false,
      selectedVoucherItem: null,
      error: null,
      reportData: {
        accountTitle: "",
        openingBalance: 0,
        closingBalance: 0,
        data: [],
      },
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      campusAddress: "",
      db: "",
      cID: "",
      dtFrom: "",
      dtTo: "",
      accountID: "",
      /** Optional: bank / bank_account / cash / cash_account → filter voucher types */
      category: "",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    ledgerMode() {
      const mode = String(this.$route.query.ledgerMode || "")
        .toLowerCase()
        .trim();
      if (mode === "receivable" || mode === "payable") return mode;
      return "";
    },
    reportTitle() {
      if (this.ledgerMode === "receivable") {
        return "Accounts Receivable Ledger";
      }
      if (this.ledgerMode === "payable") {
        return "Accounts Payable Ledger";
      }
      return "Advance Accounts Ledger";
    },
    isMyskoolAba() {
      return String(this.reportDomain || "")
        .replace(/_/g, "")
        .toLowerCase() === "myskoolaba";
    },
    ledgerItems() {
      return this.reportData.data || [];
    },
    totalDebit() {
      return this.ledgerItems.reduce(
        (sum, item) => sum + (parseFloat(item.debit) || 0),
        0
      );
    },
    totalCredit() {
      return this.ledgerItems.reduce(
        (sum, item) => sum + (parseFloat(item.credit) || 0),
        0
      );
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
    this.applyPrintOrientation();
  },
  beforeDestroy() {
    this.removePrintOrientationStyle();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db;
      this.cID = query.cID || query.cId;
      this.accountID = query.accountID;
      this.dtFrom = query.dtFrom;
      this.dtTo = query.dtTo;
      this.category = this.normalizeLedgerCategory(
        query.category || query.cashFlowCategory || ""
      );

      this.campusName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "My Skool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      if (!this.db || !this.accountID) {
        this.error = "Invalid parameters. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async syncLedgerIfNeeded() {
      if (!this.db || !this.dtFrom || !this.dtTo) return;

      let baseUrl = this.getApiBaseUrl();
      try {
        await axios.get(`${baseUrl}JournalEntry/SyncLedger`, {
          params: {
            db: this.db,
            cID: this.cID,
            dtFrom: this.dtFrom,
            dtTo: this.dtTo,
          },
          headers: this.getApiHeaders(),
        });
      } catch (error) {
        console.warn("SyncLedger failed (continuing):", error);
      }
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cId: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          // Find the campus that matches cID, fallback to first if cID is not explicitly found or is 0
          const match = response.data.data.find((c) => c.id == this.cID);
          const info = match || response.data.data[0];

          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
          this.campusAddress = info.address;
        }
      } catch (e) {
        console.error("Failed to load campus info:", e);
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;
        await this.fetchAdvanceLedgerReport();
      } catch (error) {
        console.error("Failed to load advance ledger:", error);
        this.error =
          error?.message ||
          "Network error or server unavailable. Please ensure the API is running.";
      } finally {
        this.loading = false;
      }

      this.refreshAfterSync();
    },
    normalizeLedgerCategory(raw) {
      const v = String(raw || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
      if (!v) return "";
      // AdvanceAccountsLedger: bank/bank_account → BPV+BRV; cash/cash_account → CPV+CRV
      if (v === "bank" || v === "bank_account") return "bank";
      if (v === "cash" || v === "cash_account") return "cash";
      return "";
    },
    async fetchAdvanceLedgerReport() {
      let baseUrl = this.$store.state.domain;
      if (baseUrl.endsWith("/api/")) {
        // keep as is
      } else if (!baseUrl.endsWith("/")) {
        baseUrl += "/";
      }

      const url = `${baseUrl}Accounts/AdvanceAccountsLedger`;

      const params = {
        db: this.db,
        cID: this.cID,
        accountID: this.accountID,
        dtFrom: this.dtFrom,
        dtTo: this.dtTo,
      };
      // Optional category: bank → BPV/BRV, cash → CPV/CRV
      if (this.category) {
        params.category = this.category;
      }

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${this.$store.state.userData.token}`,
          accept: "application/json",
        },
      });

      if (response.data && response.data.status === "success") {
        this.reportData = response.data;
        return;
      }

      const message = response.data?.message || "Failed to load ledger data.";
      throw new Error(message);
    },
    async refreshAfterSync() {
      if (this.syncing || this.error) return;

      this.syncing = true;
      try {
        await this.syncLedgerIfNeeded();
        await this.fetchAdvanceLedgerReport();
      } catch (error) {
        console.warn(
          "Background ledger sync refresh failed (showing loaded data):",
          error
        );
      } finally {
        this.syncing = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch (e) {
        return dateString;
      }
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return "0.00";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    formatBalance(value) {
      if (value === undefined || value === null) value = 0;
      const num = Number(value) || 0;
      const amount = this.formatCurrency(Math.abs(num));

      if (this.reportDomain !== "myskoolaba") {
        return this.formatCurrency(num);
      }

      if (num > 0) return `${amount} DR`;
      if (num < 0) return `${amount} CR`;
      return amount;
    },
    formatDebit(value) {
      return this.formatCurrency(value);
    },
    formatCredit(value) {
      return this.formatCurrency(value);
    },
    openVoucherMenu(item) {
      const entryID = item.voucherID || item.id || item.vId;
      if (!entryID) return;
      this.selectedVoucherItem = item;
      this.showVoucherAction = true;
    },
    printSelectedVoucher() {
      if (!this.selectedVoucherItem) return;
      this.printVoucher(this.selectedVoucherItem);
      this.showVoucherAction = false;
    },
    printVoucher(item) {
      const entryID = item.voucherID || item.id || item.vId;
      if (!entryID) return;

      const baseUrl = `https://${this.reportDomain}.myskool.app`;
      const url = `${baseUrl}/Account/PrintEntryVoucher?entryID=${entryID}&dbb=${encodeURIComponent(
        this.db
      )}&cID=${this.cID}`;
      window.open(url, "_blank");
    },
    resolveVoucherModeFromEntry(entry) {
      const voucherType = entry?.voucherType || "";
      if (voucherType === "BPV" || voucherType === "BRV") return "bank";
      if (voucherType === "CPV" || voucherType === "CRV") return "credit";
      return "journal";
    },
    getEditRouteName() {
      return this.reportDomain === "myskoolhpgs"
        ? "accounting-project"
        : "accounting-advance";
    },
    getApiBaseUrl() {
      let baseUrl = this.$store.state.domain;
      if (!baseUrl.endsWith("/")) {
        baseUrl += "/";
      }
      return baseUrl;
    },
    getApiHeaders() {
      return {
        Authorization: `Bearer ${this.$store.state.userData.token}`,
        accept: "application/json",
      };
    },
    findJournalListItem(items, entryID) {
      if (!Array.isArray(items)) return null;
      return items.find((row) => row.entry && row.entry.id == entryID) || null;
    },
    async fetchJournalEntryItem(entryID, vNo) {
      const baseUrl = this.getApiBaseUrl();
      const headers = this.getApiHeaders();
      const db = this.db || this.$store.state.userData.db;
      const cID = this.cID || this.$store.state.userData.cId;

      const keywords = [vNo, String(entryID)].filter(
        (keyword) => keyword && keyword !== "-"
      );

      for (const keyword of keywords) {
        try {
          const searchRes = await axios.get(`${baseUrl}JournalEntry/Search`, {
            params: { db, cID, keyword },
            headers,
          });
          const searchItems = searchRes.data?.data || searchRes.data || [];
          const searchMatch = this.findJournalListItem(searchItems, entryID);
          if (searchMatch) return searchMatch;
        } catch (error) {
          console.error("Journal entry search failed:", error);
        }
      }

      if (this.dtFrom && this.dtTo) {
        try {
          let page = 1;
          let totalPages = 1;

          while (page <= totalPages && page <= 20) {
            const paginationRes = await axios.get(
              `${baseUrl}JournalEntry/LoadDataPagination`,
              {
                params: {
                  db,
                  cID,
                  dtFrom: this.dtFrom,
                  dtTo: this.dtTo,
                  page,
                },
                headers,
              }
            );
            const payload =
              paginationRes.data?.data || paginationRes.data || paginationRes;
            const items = Array.isArray(payload) ? payload : payload.data || [];
            const paginationMatch = this.findJournalListItem(items, entryID);
            if (paginationMatch) return paginationMatch;

            const total = payload.total || 0;
            const perPage = payload.perPage || items.length || 1;
            totalPages = Math.max(1, Math.ceil(total / perPage));
            page += 1;
          }
        } catch (error) {
          console.error("Journal entry pagination load failed:", error);
        }
      }

      try {
        const loadRes = await axios.get(`${baseUrl}JournalEntry/LoadData`, {
          params: { db, cID, id: entryID },
          headers,
        });
        const payload = loadRes.data?.data || loadRes.data || loadRes;
        if (payload?.entry) {
          return {
            entry: payload.entry,
            details: payload.details || [],
          };
        }
      } catch (error) {
        console.error("Journal entry load failed:", error);
      }

      return null;
    },
    async editSelectedVoucher() {
      if (!this.selectedVoucherItem || this.editLoading) return;

      const entryID =
        this.selectedVoucherItem.voucherID ||
        this.selectedVoucherItem.id ||
        this.selectedVoucherItem.vId;
      if (!entryID) return;

      this.editLoading = true;
      try {
        const item = await this.fetchJournalEntryItem(
          entryID,
          this.selectedVoucherItem.vNo
        );

        if (!item || !item.entry) {
          this.$bvToast.toast("Unable to load voucher for editing.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
          return;
        }

        const routeName = this.getEditRouteName();
        const voucherMode = this.resolveVoucherModeFromEntry(item.entry);

        sessionStorage.setItem(
          `${JOURNAL_EDIT_STORAGE_PREFIX}${entryID}`,
          JSON.stringify(item)
        );

        const routeData = this.$router.resolve({
          name: routeName,
          params: { id: item.entry.id, data: item },
          query: { voucherMode },
        });

        this.showVoucherAction = false;
        window.open(routeData.href, "_blank");
      } catch (error) {
        console.error("Failed to open voucher for edit:", error);
        this.$bvToast.toast("Unable to open voucher for editing.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.editLoading = false;
      }
    },
    print() {
      this.applyPrintOrientation();
      window.print();
    },
    applyPrintOrientation() {
      const styleId = "advance-ledger-print-orientation";
      let styleEl = document.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      const orientation = this.isMyskoolAba ? "portrait" : "landscape";
      styleEl.textContent = `@page { size: ${orientation}; margin: 0; }`;
    },
    removePrintOrientationStyle() {
      const styleEl = document.getElementById(
        "advance-ledger-print-orientation"
      );
      if (styleEl) styleEl.remove();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.advance-ledger-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
  overflow-x: hidden;
  box-sizing: border-box;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 4rem 3rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 100%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
  overflow: hidden;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .d-flex {
    min-width: 0;
  }

  .header-text-block {
    min-width: 0;
    flex: 1;
  }

  .school-logo-img {
    height: 90px;
    width: auto;
    max-width: 120px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .school-name-full {
    font-size: 1.7rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 0;
      flex-shrink: 0;
    }

    .date-range-text {
      color: #555;
      min-width: 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
  }
}

.balance-summary {
  display: flex;
  justify-content: center;
  gap: 3rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  margin: 2rem 0;
  border: 1px solid #eee;

  .balance-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
    }
  }
}

.ledger-table-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 2rem;
}

.ledger-table {
  width: 100%;
  max-width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  border: 1px solid #dee2e6;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 8px 10px;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
    vertical-align: top;
  }

  .col-vno {
    width: 9%;
  }
  .col-date {
    width: 11%;
  }
  .col-cheque {
    width: 14%;
  }
  .col-desc {
    width: 30%;
  }
  .col-amount {
    width: 12%;
    white-space: nowrap;
  }

  .cell-vno,
  .cell-date,
  .cell-cheque,
  .cell-desc {
    line-height: 1.4;
  }

  .cell-desc {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .cell-cheque {
    overflow-wrap: anywhere;
  }

  thead {
    background: #2c3e50;
    th {
      padding: 14px 10px;
      color: white;
      font-weight: 700;
      text-align: left;
      text-transform: uppercase;
      font-size: 0.8rem;
      letter-spacing: 0.5px;
      border: 1px solid #2c3e50;
      white-space: normal;
      vertical-align: middle;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f1f2f6;
      &:hover {
        background: #fdfdfd;
      }

      td {
        padding: 10px;
        font-size: 0.9rem;
        color: #2d3436;
      }

      &.opening-row {
        background-color: #fffaf0;
        td {
          font-weight: 700;
          color: #000;
        }
      }
    }

    .total-row {
      background: #f1f3f5;
      page-break-inside: avoid;
      break-inside: avoid;

      td {
        padding: 15px 10px;
        font-weight: 800;
        border-top: 2px solid #2c3e50;
        color: #2c3e50;
      }
    }
  }

  .voucher-link {
    color: #000;
    text-decoration: none;
    font-weight: 700;
    word-break: break-all;
    display: inline-block;
    max-width: 100%;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
      color: #2e86de;
    }
  }
}

.voucher-action-body {
  text-align: center;
}

.voucher-action-label {
  color: #636e72;
  font-size: 0.95rem;
}

.voucher-action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.signature-section {
  display: flex;
  justify-content: space-between;
  margin-top: 5rem;

  .sig-box {
    text-align: center;
    width: 250px;

    .sig-line {
      border-top: 1px solid #1a1a1a;
      margin-bottom: 0.5rem;
    }

    span {
      font-weight: 600;
      color: #636e72;
      font-size: 0.9rem;
    }
  }
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
}

.text-right {
  text-align: right;
}
.text-center {
  text-align: center;
}
.font-weight-bold {
  font-weight: 700;
}
.text-italic {
  font-style: italic;
}
.bg-light {
  background-color: #f8f9fa;
}

@page {
  size: landscape;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .advance-ledger-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1cm 1.5cm !important; /* Internal margins for print */
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-table-wrap {
    overflow: visible !important;
    margin-top: 1rem !important;
  }

  .ledger-table {
    font-size: 9pt !important;
    table-layout: fixed !important;
    width: 100% !important;

    th,
    td {
      padding: 5px 6px !important;
      border: 1px solid #dee2e6 !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      word-break: break-word !important;
      white-space: normal !important;
    }

    .col-amount {
      white-space: nowrap !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-size: 8pt !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .voucher-link {
      color: #000 !important;
      text-decoration: none !important;
    }

    tbody .total-row {
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      page-break-inside: avoid;
      break-inside: avoid;
    }
  }

  .report-header-modern {
    margin-bottom: 5px !important;
    padding-bottom: 10px !important;
    .school-logo-img {
      height: 60px !important;
    }
    .school-name-full {
      font-size: 1.4rem !important;
    }
    .report-info-line {
      font-size: 0.9rem !important;
    }
  }

  .balance-summary {
    margin: 10px 0 !important;
    padding: 10px !important;
    background: #f8f9fa !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .signature-section {
    margin-top: 30px !important;
    page-break-inside: avoid;
  }

  .page-footer {
    display: none;
  }
}
</style>
